/**
 * Artwork for the tokens the UI draws, with no addresses or decimals: both are
 * the deployment's (`tokenAddress()`, `decimalsBySymbol()`), since a token's
 * decimals differ per chain (BSC's IDRX has 0).
 */

import {
  getTokenConfig,
  getTokens as getRuntimeTokens,
  getRuntimeConfig,
  type RuntimeToken,
  type TokenKind,
} from "./runtimeConfig";

export interface Token {
  symbol: string;
  name: string;
  /**
   * Fallback for the descriptor's `kind`, so the UI warns before it loads: "fiat"
   * legs are quotable, never settleable.
   */
  kind: TokenKind;
  /** Icon circle fill color. */
  color: string;
  /** Optional second color — renders the icon circle as a two-tone gradient. */
  colorB?: string;
  /** Short glyph rendered inside the icon circle (1–2 characters). */
  glyph: string;
  /** Demo balance shown in the trade panels (human units). */
  mockBalance: number;
  /** Demo USD valuation of the mock balance (hardcoded mid-rate math). */
  mockUsdValue: number;
}

export const TOKENS: readonly Token[] = [
  { symbol: "EUR", name: "Euro", kind: "fiat", color: "#1a3c8f", glyph: "€", mockBalance: 316119.37, mockUsdValue: 344570.11 },
  { symbol: "EURC", name: "Euro Coin", kind: "erc20", color: "#2775ca", glyph: "€", mockBalance: 181000, mockUsdValue: 197290.0 },
  { symbol: "USD", name: "US Dollar", kind: "fiat", color: "#1f8a4c", glyph: "$", mockBalance: 12000, mockUsdValue: 12000.0 },
  { symbol: "USDC", name: "USD Coin", kind: "erc20", color: "#2775ca", glyph: "$", mockBalance: 84210.55, mockUsdValue: 84210.55 },
  { symbol: "USDT", name: "Tether USD", kind: "erc20", color: "#26a17b", glyph: "₮", mockBalance: 210000, mockUsdValue: 209790.0 },
  { symbol: "CADC", name: "CAD Coin", kind: "erc20", color: "#d52b1e", glyph: "C$", mockBalance: 140000, mockUsdValue: 100436.0 },
  { symbol: "TRYB", name: "BiLira", kind: "erc20", color: "#d92332", glyph: "₺", mockBalance: 1500000, mockUsdValue: 44250.0 },
  { symbol: "BBRL", name: "Braza BRL", kind: "erc20", color: "#c8102e", colorB: "#0b7a3e", glyph: "R$", mockBalance: 120000, mockUsdValue: 22200.0 },
  { symbol: "BRZ", name: "BRZ Token", kind: "erc20", color: "#00a868", glyph: "R$", mockBalance: 5400000, mockUsdValue: 999000.0 },
  { symbol: "IDRX", name: "IDRX", kind: "erc20", color: "#d81e34", glyph: "Rp", mockBalance: 16500000, mockUsdValue: 1006.5 },
] as const;

/** The preferred opening pair; `defaultPair()` resolves it against what the deployment offers. */
export const DEFAULT_SELL_TOKEN = "CADC";
export const DEFAULT_BUY_TOKEN = "USDC";

export function tokenBySymbol(symbol: string): Token {
  const runtime = getTokenConfig(symbol);
  const local = TOKENS.find((t) => t.symbol === symbol);
  if (runtime) return local ? { ...local, name: runtime.name ?? local.name } : synthesizeToken(runtime);
  return local ?? synthesizeToken({ symbol, name: symbol || "Select token", address: null, decimals: null, kind: "erc20" });
}

/**
 * A token the deployment offers with no artwork here: a generic badge, and zero
 * rather than invented mock balances.
 */
function synthesizeToken(runtime: RuntimeToken): Token {
  return {
    symbol: runtime.symbol,
    name: runtime.name ?? runtime.symbol,
    kind: runtime.kind,
    color: "#6b7280",
    glyph: runtime.symbol.slice(0, 2),
    mockBalance: 0,
    mockUsdValue: 0,
  };
}

/**
 * What the UI offers: the deployment's tokens in descriptor order (`TOKENS` is
 * only artwork), or the whole catalogue before it loads, so the picker is never empty.
 */
export function offeredTokens(): readonly Token[] {
  const markets = getRuntimeConfig()?.markets;
  const runtime = markets?.tokens ?? getRuntimeTokens();
  if (runtime.length === 0) return markets === undefined ? TOKENS : [];
  return runtime.map((t) => TOKENS.find((candidate) => candidate.symbol === t.symbol) ?? synthesizeToken(t));
}

/**
 * The pair to open the trade card on: the preferred one when the deployment can
 * trade it, else the first two it can. Trading needs an address (a token can be
 * offered before it is deployed) and no fiat leg, which no solver fills.
 */
export function defaultPair(): { sell: string; buy: string } {
  const markets = getRuntimeConfig()?.markets;
  if (markets) {
    const direction = markets.directions.find((d) => d.sellToken === DEFAULT_SELL_TOKEN && d.buyToken === DEFAULT_BUY_TOKEN) ?? markets.directions[0];
    return { sell: direction?.sellToken ?? "", buy: direction?.buyToken ?? "" };
  }
  const offered = offeredTokens();
  const settleable = offered.filter(
    (t) => !isFiatLeg(t.symbol) && tokenAddress(t.symbol) !== null,
  );
  const has = (symbol: string): boolean => settleable.some((t) => t.symbol === symbol);
  if (has(DEFAULT_SELL_TOKEN) && has(DEFAULT_BUY_TOKEN)) {
    return { sell: DEFAULT_SELL_TOKEN, buy: DEFAULT_BUY_TOKEN };
  }
  const [first, second] = settleable;
  if (first === undefined || second === undefined) {
    // Nothing settleable: keep the preferred pair; the card's address checks refuse to sign anyway.
    return { sell: DEFAULT_SELL_TOKEN, buy: DEFAULT_BUY_TOKEN };
  }
  return { sell: first.symbol, buy: second.symbol };
}

/**
 * `null` when the deployment has none or the descriptor has not loaded: refuse,
 * never substitute a placeholder.
 */
export function tokenAddress(symbol: string): string | null {
  return getTokenConfig(symbol)?.address ?? null;
}

/**
 * A display-only fiat leg: its placeholder address is no contract, so an order
 * with it is accepted and never filled. Either source's flag is decisive, so a
 * descriptor without `kind` cannot make EUR settleable.
 */
export function isFiatLeg(symbol: string): boolean {
  if (getTokenConfig(symbol)?.kind === "fiat") return true;
  return TOKENS.find((t) => t.symbol === symbol)?.kind === "fiat";
}

/**
 * Worth one US dollar by construction. Only for the USD figure under a trade
 * panel: a pegged leg's amount is the trade's dollar value, and with neither
 * leg pegged the line stays empty rather than guess a rate.
 */
const USD_PEGGED = new Set(["USD", "USDC", "USDT"]);

export function isUsdPegged(symbol: string): boolean {
  return USD_PEGGED.has(symbol);
}

/** The descriptor's decimals only: null when it does not name the token, and callers show no amount. */
export function decimalsBySymbol(symbol: string): number | null {
  return getTokenConfig(symbol)?.decimals ?? null;
}
